import React from 'react';
import * as d3 from 'd3';

import { Grid, Card, CardContent, Typography, Tooltip, Box } from '@mui/material';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import LockSharpIcon from '@material-ui/icons/LockSharp';
import { ListingVal } from 'src/interfaces/idashboard';
import {
  COLOR_BACKGROUND_2,
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_1_600,
  COLOR_SECONDARY_1,
  COLOR_SECONDARY_1_600,
  COLOR_SECONDARY_2,
  COLOR_TERTIARY_1,
  RED_BUTTON_COLOR,
} from 'src/colors';
import {
  convertCurrency,
  formatCurrency,
  getUserFriendlyPropertySizeName,
} from 'src/utils/functions';
import { BiaxialHorizontalBarChart } from 'src/App/Components/Charts';
import { COLOR_E, COLOR_PRIMARY_LIGHT, POINTER_GREEN, exchangeRates } from 'src/const';
import CircularProgress from '@mui/material/CircularProgress';
import LoadingCard from './LoadingCard';

// styles
const cardTitle = {
  fontSize: 13,
  fontWeight: 600,
};

const calculateMedian = (listings: ListingVal[], metricsKey: string) => {
  let median: number | null = null;
  let values: number[] = [];

  listings.forEach(listing => {
    listing[`${metricsKey}`] && values.push(listing[`${metricsKey}`]);
  });

  if (values.length > 0) {
    median = Math.round(d3.quantile(values, 0.5));
  }
  return median;
};

const getMedianValuesByPropertySize = (
  listings: ListingVal[],
  metricsKeys: string[],
  currency: string | null = null
) => {
  let bedroomDistro: { [key: string]: { [key: string]: number[] } } = {
    '0': {},
    '1': {},
    '2': {},
    '3': {},
    '4': {},
    '5': {},
    '6+': {},
    rooms: {},
  };

  Object.keys(bedroomDistro).forEach(bedroom => {
    metricsKeys.forEach(metricsKey => {
      bedroomDistro[bedroom][metricsKey] = [];
    });
  });

  listings.forEach((listing: ListingVal) => {
    metricsKeys.forEach(metricsKey => {
      const value: number = listing[`${metricsKey}`];
      if (
        ((listing.room_type && listing.room_type === 'entire_home') ||
          (listing.property_type && listing.property_type === 'condo')) &&
        (listing.bedrooms == 'Studio' || !isNaN(Number(listing.bedrooms)))
      ) {
        if (listing.bedrooms == 'Studio') {
          value && bedroomDistro['0'][metricsKey].push(value);
        } else if (Number(listing.bedrooms) >= 6) {
          value && bedroomDistro['6+'][metricsKey].push(value);
        } else {
          value && bedroomDistro[listing.bedrooms][metricsKey].push(value);
        }
      } else if (
        listing.room_type &&
        (listing.room_type === 'private_room' ||
          listing.room_type === 'hotel_room' ||
          listing.room_type === 'shared_room')
      ) {
        value && bedroomDistro['rooms'][metricsKey].push(value);
      }
    });
  });

  const result = Object.keys(bedroomDistro).map(key => {
    const metrics = metricsKeys.reduce((acc, metricKey) => {
      const prettifiedMetricKey = prettyfyKey(metricKey);
      acc[prettifiedMetricKey] =
        currency && metricKey.toLocaleLowerCase().includes('revenue')
          ? convertCurrency(currency, d3.quantile(bedroomDistro[key][metricKey], 0.5))
          : (d3.quantile(bedroomDistro[key][metricKey], 0.5) as number);
      return acc;
    }, {});

    return {
      keyShort: getUserFriendlyPropertySizeName(key, true),
      keyLong: getUserFriendlyPropertySizeName(key),
      ...metrics,
    };
  });

  return result;
};

const prettyfyKey = key => {
  if (key == 'revenuePotential') return 'Revenue';
  else if (key == 'occupancyRateLTM') return 'Occupancy Rate';
  else return '';
};

const NeighbourhoodDataCard = ({
  listings,
  currency,
  width,
  height,
  isUserAllowed,
  multipleMarkersCallLoading,
  handleOnSampleClick,
}: {
  listings: ListingVal[];
  currency: string;
  width: number;
  height: number;
  isUserAllowed: boolean;
  multipleMarkersCallLoading: Boolean;
  handleOnSampleClick: any;
}) => {
  const numberOfListings = listings.length;
  const medianAdrLTM = calculateMedian(listings, 'adrLTM');
  const medianOccupancyRateLTM = calculateMedian(listings, 'occupancyRateLTM');
  const medianRevenueLTM = calculateMedian(listings, 'revenuePotential');

  const medianValueseByPropertySize = getMedianValuesByPropertySize(
    listings,
    ['revenuePotential', 'occupancyRateLTM'],
    currency
  );

  return (
    <Card sx={{ width: width, height: height, position: 'relative' }}>
      <Tooltip title="Save Neighbourhood - coming soon!">
        <FavoriteBorderIcon
          sx={{ position: 'absolute', top: 1, right: 1, color: RED_BUTTON_COLOR }}
        />
      </Tooltip>
      <CardContent sx={{ width: '100%', height: '100%' }}>
        <Typography variant="h6">Neighbourhood</Typography>
        <Grid
          container
          direction="column"
          justifyContent="flex-start"
          alignItems="center"
          sx={{ background: COLOR_BACKGROUND_2, width: '100%' }}
          spacing={2}
        >
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            item
            xs={12}
          >
            <Grid container direction="column" item xs={3}>
              <Typography variant="h2" style={cardTitle}>
                Revenue
              </Typography>

              <Typography
                sx={{
                  fontWeight: 900,
                  color: COLOR_PRIMARY_1,
                  display: 'flex',
                  alignItems: 'center',
                }}
                variant="h6"
                component="h3"
              >
                {exchangeRates[currency].symbol}
                {isUserAllowed ? (
                  multipleMarkersCallLoading ? (
                    <CircularProgress size={20} sx={{ color: 'inherit' }} />
                  ) : (
                    formatCurrency(convertCurrency(currency, medianRevenueLTM as number).toString())
                  )
                ) : (
                  <Box
                    onClick={handleOnSampleClick}
                    sx={{ '&:hover': { cursor: 'pointer' }, marginTop: '5px' }}
                  >
                    <LockSharpIcon style={{ fontSize: 24, color: 'inherit' }} />
                  </Box>
                )}
              </Typography>
            </Grid>
            <Grid container direction="column" item xs={3}>
              <Typography variant="h2" style={cardTitle}>
                Occupancy Rate
              </Typography>

              <Typography
                sx={{
                  fontWeight: 900,
                  color: COLOR_SECONDARY_1,
                  display: 'flex',
                  alignItems: 'center',
                }}
                variant={'h6'}
                component="h3"
              >
                {isUserAllowed ? (
                  multipleMarkersCallLoading ? (
                    <CircularProgress size={20} sx={{ color: 'inherit' }} />
                  ) : (
                    medianOccupancyRateLTM
                  )
                ) : (
                  <Box
                    onClick={handleOnSampleClick}
                    sx={{ '&:hover': { cursor: 'pointer' }, marginTop: '5px' }}
                  >
                    <LockSharpIcon style={{ fontSize: 24, color: 'inherit' }} />
                  </Box>
                )}
                %
              </Typography>
            </Grid>
            <Grid container direction="column" item xs={3}>
              <Typography variant="h2" style={cardTitle}>
                Daily Rate
              </Typography>

              <Typography
                sx={{
                  fontWeight: 900,
                  color: COLOR_SECONDARY_2,
                  display: 'flex',
                  alignItems: 'center',
                }}
                variant={'h6'}
                component="h3"
              >
                {exchangeRates[currency].symbol}
                {!multipleMarkersCallLoading ? (
                  formatCurrency(convertCurrency(currency, medianAdrLTM as number).toString())
                ) : (
                  <CircularProgress size={20} sx={{ color: 'inherit' }} />
                )}
              </Typography>
            </Grid>
            <Grid container direction="column" item xs={3}>
              <Typography variant="h2" style={cardTitle}>
                No. of Listings
              </Typography>

              <Typography
                sx={{ fontWeight: 900, color: COLOR_TERTIARY_1 }}
                variant={'h6'}
                component="h3"
              >
                {numberOfListings}
              </Typography>
            </Grid>
          </Grid>

          <Grid container direction="column" item xs={12}>
            <Typography variant="h2" sx={{ ...cardTitle, paddingBottom: 2 }}>
              <span style={{ color: COLOR_PRIMARY_1 }}>Revenue</span> &{' '}
              <span style={{ color: COLOR_SECONDARY_1 }}>Occupancy Rate</span> by Property Size
            </Typography>
            <BiaxialHorizontalBarChart
              data={medianValueseByPropertySize}
              label="Bedrooms"
              height={150}
              units={[exchangeRates[currency].symbol, '%']}
              barColors={[COLOR_PRIMARY_1_600, COLOR_SECONDARY_1_600]}
              barAccentColors={[COLOR_PRIMARY_1, COLOR_SECONDARY_1]}
              useShortHand={true}
              isCurrency={[true, false]}
              isUserAllowed={isUserAllowed}
              multipleMarkersCallLoading={multipleMarkersCallLoading}
            />

            {!isUserAllowed && (
              <div
                style={{
                  height: '39%',
                  position: 'absolute',
                  top: '125px',
                  left: '46px',
                  width: '74%',
                  background: '#ffffff',
                  filter: 'blur( 6px)',
                  opacity: 0.8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              ></div>
            )}
            {!isUserAllowed && (
              <Typography
                sx={{
                  position: 'absolute',
                  top: '60%',
                  left: '50%',
                  fontSize: '26px',
                  fontWeight: 900,
                  color: COLOR_E,
                  transform: 'translate(-50%, -50%)',
                  cursor: 'pointer',
                }}
                onClick={handleOnSampleClick}
              >
                PREMIUM
              </Typography>
            )}
            {isUserAllowed && multipleMarkersCallLoading && (
              <Box
                sx={{
                  height: '80px',
                  width: '80%',
                  position: 'absolute',
                  top: '115px',
                  left: '50px',
                }}
              >
                <LoadingCard height={80} type="bars" barCount={1} />
              </Box>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default NeighbourhoodDataCard;
